import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { Inbox, FileSpreadsheet, Users, Banknote, Cpu, X } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import { AdminShell } from '@/components/admin-shell';
import { api } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';

interface DashboardData {
  contabilidad: {
    facturasPendientes: number;
    facturasMes: number;
    declaracionesPendientes: number;
    declaracionesMes: number;
  };
  contribuyentes: { activos: number; nuevosMes: number };
  cobros: { solicitudesMes: number; montoMes: number };
  ia: { costoMes: number; tokensMes: number };
  charts: {
    facturasPorMes: Array<{ label: string; count: number }>;
    cobrosPorMes: Array<{ label: string; monto: number }>;
    facturasPorEstado: Array<{ estado: string; count: number }>;
    iaPorDia: Array<{ label: string; costo: number }>;
  };
}

const PANEL = 'rounded-lg border border-border bg-background shadow-sm';
const nf = new Intl.NumberFormat('es-PY');
const fmtGs = (n: number) => `Gs ${nf.format(Math.round(n))}`;
const fmtUsd = (n: number) => `US$ ${n.toFixed(4)}`;
const fmtK = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(Math.round(n)));

const STATUS_LABEL: Record<string, string> = {
  pending_extraction: 'Procesando',
  pending_review: 'En revisión',
  approved: 'Aprobadas',
  included_in_declaration: 'Incluidas',
  rejected: 'Rechazadas',
};
const STATUS_COLOR: Record<string, string> = {
  pending_extraction: 'hsl(var(--chart-4))',
  pending_review: 'hsl(var(--chart-3))',
  approved: 'hsl(var(--chart-2))',
  included_in_declaration: 'hsl(var(--chart-1))',
  rejected: 'hsl(var(--destructive))',
};

const facturasConfig = {
  count: { label: 'Facturas', color: 'hsl(var(--chart-1))' },
} satisfies ChartConfig;
const cobrosConfig = {
  monto: { label: 'Monto', color: 'hsl(var(--chart-2))' },
} satisfies ChartConfig;
const iaConfig = {
  costo: { label: 'Costo', color: 'hsl(var(--chart-1))' },
} satisfies ChartConfig;

const AXIS = { tickLine: false, axisLine: false, fontSize: 12, stroke: 'hsl(var(--muted-foreground))' };

function Kpi({
  icon,
  label,
  value,
  hint,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  href?: string;
}) {
  const inner = (
    <div className="h-full rounded-lg border border-border bg-background p-4 shadow-sm transition-colors hover:border-primary/40">
      <div className="mb-1 flex items-center gap-1.5 text-sm text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold tabular-nums text-foreground">{value}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
  return href ? (
    <Link href={href} className="block">
      {inner}
    </Link>
  ) : (
    inner
  );
}

function ChartPanel({
  title,
  children,
  hasData,
}: {
  title: string;
  children: React.ReactNode;
  hasData: boolean;
}) {
  return (
    <div className={`${PANEL} p-4`}>
      <h3 className="mb-3 text-sm font-semibold text-foreground">{title}</h3>
      {hasData ? (
        children
      ) : (
        <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
          Sin datos todavía.
        </div>
      )}
    </div>
  );
}

export function AdminDashboardPage() {
  // El rango filtra SOLO los gráficos de serie (las KPIs usan ventanas fijas).
  const [range, setRange] = useState<DateRange | undefined>();
  const from = range?.from ? format(range.from, 'yyyy-MM-dd') : undefined;
  const to = range?.to ? format(range.to, 'yyyy-MM-dd') : undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-dashboard', from ?? null, to ?? null],
    queryFn: () =>
      api<DashboardData>(
        from && to ? `/api/admin/dashboard?from=${from}&to=${to}` : '/api/admin/dashboard'
      ),
    placeholderData: keepPreviousData,
  });

  // Config del donut: una entrada por estado → label en español + color del token.
  // Permite que el ChartTooltipContent resuelva el nombre y los Cell usen var(--color-<estado>).
  const estadoConfig = useMemo<ChartConfig>(() => {
    const cfg: ChartConfig = {};
    for (const e of data?.charts.facturasPorEstado ?? []) {
      cfg[e.estado] = {
        label: STATUS_LABEL[e.estado] ?? e.estado,
        color: STATUS_COLOR[e.estado] ?? 'hsl(var(--chart-5))',
      };
    }
    return cfg;
  }, [data]);

  return (
    <AdminShell>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Inicio</h1>
          <p className="text-sm text-muted-foreground">Visión general del día a día.</p>
        </div>
        <div className="flex items-center gap-1.5">
          <DateRangePicker
            value={range}
            onChange={setRange}
            placeholder="Rango (gráficos)"
            align="end"
            disabled={{ after: new Date() }}
            className="w-auto"
          />
          {range?.from && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setRange(undefined)}
              title="Limpiar rango"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      </div>

      {isLoading || !data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className="space-y-6">
          {/* KPIs accionables */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <Kpi
              icon={<Inbox className="size-4" />}
              label="A revisar"
              value={String(data.contabilidad.facturasPendientes)}
              hint="facturas pendientes"
              href="/admin/review-queue"
            />
            <Kpi
              icon={<FileSpreadsheet className="size-4" />}
              label="Declar. pendientes"
              value={String(data.contabilidad.declaracionesPendientes)}
              href="/admin/declarations"
            />
            <Kpi
              icon={<Users className="size-4" />}
              label="Contactos activos"
              value={String(data.contribuyentes.activos)}
              hint={`+${data.contribuyentes.nuevosMes} este mes`}
              href="/admin/tenants"
            />
            <Kpi
              icon={<Banknote className="size-4" />}
              label="Cobros del mes"
              value={String(data.cobros.solicitudesMes)}
              hint={fmtGs(data.cobros.montoMes)}
              href="/admin/cobros"
            />
            <Kpi
              icon={<Cpu className="size-4" />}
              label="Costo IA (30d)"
              value={fmtUsd(data.ia.costoMes)}
              hint={`${fmtK(data.ia.tokensMes)} tokens`}
              href="/admin/ia"
            />
          </div>

          {/* Gráficos */}
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartPanel
              title="Facturas por mes"
              hasData={data.charts.facturasPorMes.some((d) => d.count > 0)}
            >
              <ChartContainer config={facturasConfig} className="aspect-auto h-[220px] w-full">
                <BarChart data={data.charts.facturasPorMes} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" {...AXIS} />
                  <YAxis {...AXIS} width={32} allowDecimals={false} />
                  <ChartTooltip cursor={{ fill: 'hsl(var(--muted))' }} content={<ChartTooltipContent />} />
                  <Bar dataKey="count" fill="var(--color-count)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartContainer>
            </ChartPanel>

            <ChartPanel
              title="Cobros por mes (Gs)"
              hasData={data.charts.cobrosPorMes.some((d) => d.monto > 0)}
            >
              <ChartContainer config={cobrosConfig} className="aspect-auto h-[220px] w-full">
                <BarChart data={data.charts.cobrosPorMes} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" {...AXIS} />
                  <YAxis {...AXIS} width={48} tickFormatter={(v: number) => fmtK(v)} />
                  <ChartTooltip
                    cursor={{ fill: 'hsl(var(--muted))' }}
                    content={
                      <ChartTooltipContent
                        formatter={(value) => (
                          <div className="flex flex-1 items-center justify-between gap-2 leading-none">
                            <span className="text-muted-foreground">Monto</span>
                            <span className="font-mono font-medium tabular-nums">{fmtGs(Number(value))}</span>
                          </div>
                        )}
                      />
                    }
                  />
                  <Bar dataKey="monto" fill="var(--color-monto)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartContainer>
            </ChartPanel>

            <ChartPanel
              title="Facturas por estado"
              hasData={data.charts.facturasPorEstado.length > 0}
            >
              <div className="flex items-center gap-4">
                <ChartContainer config={estadoConfig} className="aspect-auto h-[220px] w-3/5">
                  <PieChart>
                    <ChartTooltip content={<ChartTooltipContent nameKey="estado" hideLabel />} />
                    <Pie
                      data={data.charts.facturasPorEstado}
                      dataKey="count"
                      nameKey="estado"
                      innerRadius={52}
                      outerRadius={84}
                      paddingAngle={2}
                    >
                      {data.charts.facturasPorEstado.map((e) => (
                        <Cell key={e.estado} fill={`var(--color-${e.estado})`} />
                      ))}
                    </Pie>
                  </PieChart>
                </ChartContainer>
                <div className="flex-1 space-y-1.5">
                  {data.charts.facturasPorEstado.map((e) => (
                    <div key={e.estado} className="flex items-center justify-between gap-2 text-sm">
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block size-2.5 rounded-sm"
                          style={{ background: STATUS_COLOR[e.estado] ?? 'hsl(var(--chart-5))' }}
                        />
                        {STATUS_LABEL[e.estado] ?? e.estado}
                      </span>
                      <span className="font-semibold tabular-nums">{e.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </ChartPanel>

            <ChartPanel
              title="Costo IA por día (US$)"
              hasData={data.charts.iaPorDia.some((d) => d.costo > 0)}
            >
              <ChartContainer config={iaConfig} className="aspect-auto h-[220px] w-full">
                <AreaChart data={data.charts.iaPorDia} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="iaFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-costo)" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="var(--color-costo)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" {...AXIS} />
                  <YAxis {...AXIS} width={48} tickFormatter={(v: number) => `$${v.toFixed(3)}`} />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        formatter={(value) => (
                          <div className="flex flex-1 items-center justify-between gap-2 leading-none">
                            <span className="text-muted-foreground">Costo</span>
                            <span className="font-mono font-medium tabular-nums">{fmtUsd(Number(value))}</span>
                          </div>
                        )}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="costo"
                    stroke="var(--color-costo)"
                    strokeWidth={2}
                    fill="url(#iaFill)"
                  />
                </AreaChart>
              </ChartContainer>
            </ChartPanel>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
